import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack } from '@tanium/react-stack';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';

interface ModalProps {
  isOpen: boolean;
  instanceId: string;
  instanceName: string;
  onRequestClose: () => void;
}

export const DeactivateCEModal: React.FC<ModalProps> = ({ isOpen, instanceId, instanceName, onRequestClose }) => {
  const { t } = useTranslation();
  const { deactivateCE } = useTaniumInstances();
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (deactivateCE.status === AsyncStatus.Success) {
      onRequestClose();
      deactivateCE.reset();
      showNotification({
        type: 'success',
        contents: t('Successfully initiated deactivation of instanceName', { instanceName }),
      });
    } else if (deactivateCE.status === AsyncStatus.Failure) {
      onRequestClose();
      deactivateCE.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to deactivate instanceName', { instanceName }),
      });
    }
  }, [onRequestClose, deactivateCE, showNotification, t, instanceName]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Deactivate Tanium Instance')}
      confirmLabel={t('Deactivate')}
      cancelLabel={t('Close')}
      onConfirm={async () => {
        await deactivateCE.execute(instanceId);
      }}
      isConfirmDisabled={deactivateCE.status === AsyncStatus.Pending}
      onCancel={() => {
        onRequestClose();
        deactivateCE.reset();
      }}
    >
      <Stack direction="column" itemSpacing={2}>
        <span>
          {t(
            'Deactivating the Tanium instance stops all scheduled actions from running. Actions in progress will complete their current run. The Tanium Console for this instance will be unavailable to all users.'
          )}
        </span>
        <Notification role="status" type="warning">
          {t('Deactivated instances are deleted after being deactivated for 30 days.')}
        </Notification>
      </Stack>
    </ConfirmModal>
  );
};
